import { useContext, useEffect, useState } from "react";
import { useAuthState as useFirebaseAuthState } from "react-firebase-hooks/auth";
import { getConfigs } from "../../common/api/configApi";
import { UserContext } from "../../common/hooks/UserContext";
import { setConfigsLocal } from "../../illustrations/models/illustrationConfigs";
import { setCookie } from "../../common/utils/cookiesHandler";
import {
  addSuccessfulLoginLog,
  isUserEqual,
  signInToFirebase,
} from "../api/authApi";
import { getUserDetails, setUserPermittedActions } from "../api/userApi";
import { isEmailValid } from "../validations/authenticationValidations";
import { useGoogleAuthState } from "./useGoogleAuthState";

/**
 * Hook which handles user authentication with google and firebase.
 * This hook was made to encapsulate logging the process of logging in the user to google first and to firebase.
 * Before this the project was using google login provided by firebase but it was decided to move to this way due to
 * the following reasons:
 * - To call the cloud functions securely, `access_token` from google auth object was needed
 * - Google login provided by firebase only gives this `access_token` logs in explicitly and doesn't provide incase the
 * user is already logged in.
 * - By using google login separately, `access_token` is accessible at all times.
 *
 * @param {Object} firebaseAuth - Firebase Auth object
 * @return {Array} - Array [user, loading, error, signIn, signOut].
 * user : Contains the user object if it is there else null
 * loading : True if fetching user object or initializing, else false
 * error : Contains error object if any error occurred, else null
 * signIn : Function to signIn the user
 * signOut : Function to signOut the user
 */
export function useAuthState(firebaseAuth, fromLogin = false) {
  // Define user, loading and error state variables which will be returned from this hook
  const [userState, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setUser } = useContext(UserContext);
  const userUid = userState?.uid;

  const [firebaseUser, firebaseInitialising, firebaseError] =
    useFirebaseAuthState(firebaseAuth);

  useEffect(() => {
    setUser(userState);
    if(fromLogin) {
      setCookie("user", JSON.stringify(userState), 50);
    }
  }, [userUid, setUser, userState, fromLogin]);

  const [
    googleUser,
    googleInitialising,
    googleError,
    signIn,
    signOut,
    onLoginSuccess,
  ] = useGoogleAuthState();

  useEffect(() => {
    async function getUserDetailsAndSetUserPermittedActions(
      firebaseUser,
      googleUid
    ) {
      // Get user details
      var userObj = await getUserDetails(firebaseUser, googleUid);
      // Set user permitted actions
      await setUserPermittedActions(userObj.role);
      // Get illustration configs
      var configs = await getConfigs();
      // Save illustration configs in memory
      setConfigsLocal(configs);
      return userObj;
    }

    // Error from google login or firebase login
    if (firebaseError || googleError) {
      setLoading(false);
      setUserState(null);
      setError(googleError ? googleError : firebaseError);
    }

    // Signed in to firebase but not signed in to google
    if (
      !googleInitialising &&
      !firebaseInitialising &&
      !googleUser &&
      firebaseUser
    ) {
      setUserState(null);
      setLoading(false);
      setError(null);
    }

    // Signed in to google but not firebase
    if (googleUser && !firebaseInitialising && !firebaseUser) {
      if (!isEmailValid(googleUser.profileObj.email)) {
        /* The google email used to log in is not a valid gojek email id
                In this case we log out the user and pass true for isInvalidEmailError */
        signOut(true);
        return;
      }
      signInToFirebase(googleUser);
    }

    // Signed in to google and signed in to firebase and both have the same user signed in
    if (googleUser && firebaseUser && isUserEqual(googleUser, firebaseUser)) {
      if (!userState) {
        getUserDetailsAndSetUserPermittedActions(
          firebaseUser,
          googleUser.profileObj.sub
        ).then((userObj) => {
          console.log({ ...userObj, idToken: googleUser.id_token });
          addSuccessfulLoginLog(userObj);
          setUserState({ ...userObj, idToken: googleUser.id_token });
          setLoading(false);
          setError(null);
        });
      }
    }

    // Signed in to google and signed in to firebase but both have different users signed in (edge case)
    if (googleUser && firebaseUser && !isUserEqual(googleUser, firebaseUser)) {
      setUserState(null);
      setLoading(false);
      setError(new Error("Google and Firebase logins dont match"));
      signOut();
    }

    // User isn't signed in to google or firebase (login page should be shown in this case)
    if (
      !googleInitialising &&
      !googleUser &&
      !firebaseInitialising &&
      !firebaseUser
    ) {
      setLoading(false);
    } // eslint-disable-next-line
  }, [
    firebaseUser,
    firebaseInitialising,
    firebaseError,
    googleUser,
    googleInitialising,
    googleError,
  ]);
  return { userState, loading, error, signIn, signOut, onLoginSuccess };
}
